// ConfirmQueue.jsx
//
// Sophia books appointments; they die unless a human rings to confirm them.
// Measured over appointments since 30 June 2026:
//
//     confirmed by a human   16 booked -> 0  cancelled   (0%)
//     never confirmed        46 booked -> 46 cancelled   (100%)
//
// So this is not a nice-to-have list. Two pieces live here:
//
//   ConfirmQueuePanel — a block pinned to the TOP of Today's Mission. A row
//     leaves it only when someone CONFIRMS or RELEASES the slot. "No answer"
//     records the attempt and puts the row back in four hours; it does not
//     clear the task, because the task is "ring this person", not "click a
//     button".
//
//   ConfirmBookingAlert — a global popup that fires the moment Sophia books,
//     so the front desk can ring while the client is still by the phone
//     instead of finding out tomorrow.
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase";

const C = {
  surface:"#ffffff", border:"#e8e2d8", text:"#1a1a2e", muted:"#6b6356",
  light:"#f4f0ea", gold:"#b48c3c", goldD:"#8a6a2a",
  red:"#b54040", redL:"rgba(181,64,64,0.10)",
  green:"#2d8a55", greenL:"rgba(45,138,85,0.10)",
  amber:"#b8860b", amberL:"rgba(184,134,11,0.12)",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return p || "";
};
const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, day] = String(d).split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
};
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2,"0")} ${ap}`;
};
const waitingLabel = (bookedAt) => {
  if (!bookedAt) return "";
  const hrs = (Date.now() - new Date(bookedAt).getTime()) / 3600000;
  if (hrs < 1) return "just booked";
  if (hrs < 48) return `waiting ${Math.round(hrs)}h`;
  return `waiting ${Math.round(hrs / 24)} days`;
};

export function useConfirmQueue() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data } = await supabase
      .from("verbal_confirmation_queue")
      .select("*")
      .order("date")
      .order("time");
    setRows(data || []);
  }, []);
  useEffect(() => {
    load();
    const ch = supabase.channel("confirm-queue-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "*", schema: "public", table: "appointments" }, load)
      .subscribe();
    const t = setInterval(load, 120000);
    return () => { supabase.removeChannel(ch); clearInterval(t); };
  }, [load]);
  return { rows, reload: load };
}

// ─── The pinned panel in Today's Mission ────────────────────────────────────
export function ConfirmQueuePanel({ userName }) {
  const { rows, reload } = useConfirmQueue();
  const [busy, setBusy] = useState(null);

  if (!rows.length) return null;
  const neverTried = rows.filter((r) => !r.confirm_attempts).length;

  const confirm = async (r) => {
    setBusy(r.id);
    await supabase.from("appointments").update({
      verbal_confirmed_at: new Date().toISOString(),
      verbal_confirmed_by: userName || "front_desk",
      needs_verbal_confirmation: false,
    }).eq("id", r.id);
    await reload(); setBusy(null);
  };
  const noAnswer = async (r) => {
    setBusy(r.id);
    // Records the dial. The row returns in four hours - it is not cleared,
    // because nobody has actually confirmed anything yet.
    await supabase.rpc("log_confirmation_attempt", {
      p_appointment_id: r.id, p_by: userName || "front_desk", p_outcome: "no_answer",
    });
    await reload(); setBusy(null);
  };
  const release = async (r) => {
    // ★ Owner 2026-10-09: releasing used to cancel silently — the client never
    //   learned their slot was gone. Now they get a text + email right away
    //   saying why: no card on file, or we couldn't reach them to confirm.
    const { data: client } = r.cid
      ? await supabase.from("clients").select("cardOnFile,card_on_file").eq("id", r.cid).maybeSingle()
      : { data: null };
    const reason = (client?.cardOnFile || client?.card_on_file) ? "unconfirmed" : "no_card";
    const why = reason === "no_card" ? "no card on file" : "we couldn't reach them to confirm";
    if (!window.confirm(`Release ${fmtDate(r.date)} ${fmtTime(r.time)}?\n\nThe slot goes back on the calendar as free, and ${r.cn || "the client"} gets a text + email that it was cancelled (${why}).`)) return;
    setBusy(r.id);
    await supabase.from("appointments").update({
      status: "cancelled",
      confirm_released_at: new Date().toISOString(),
      cancelled_at: new Date().toISOString(),
      cancelled_by_name: userName || "front_desk",
      notes: reason === "no_card"
        ? "Released — no card on file after repeated confirmation attempts"
        : "Released — no answer after repeated confirmation attempts",
    }).eq("id", r.id);

    const sent = [];
    const fail = [];
    const common = { service: r.treat || "appointment", provider: r.prov || "", date: r.date, time: r.time, reason, staff_confirmed: true };
    if (r.phone) {
      const { data, error } = await supabase.functions.invoke("notify-appointment-change", {
        body: { type: "cancellation", client_name: r.cn || "Client", client_phone: r.phone, force: true, ...common },
      });
      (error || data?.error || data?.skipped ? fail : sent).push("text");
    }
    if (r.email) {
      const { data, error } = await supabase.functions.invoke("send-appointment-email", {
        body: { type: "cancellation", to_email: r.email, to_name: r.cn || "Client", ...common },
      });
      (error || data?.error || data?.skipped ? fail : sent).push("email");
    }
    await reload(); setBusy(null);
    window.alert(
      `Released.\n\n` +
      (sent.length ? `✓ ${r.cn || "Client"} was notified by ${sent.join(" + ")}.\n` : "") +
      (fail.length ? `⚠ The ${fail.join(" + ")} did not go out — please contact them directly.\n` : "") +
      (!r.phone && !r.email ? "⚠ No phone or email on file — please contact them directly.\n" : "")
    );
  };

  return (
    <div style={{
      padding:16, marginBottom:18, borderRadius:12,
      background:"linear-gradient(135deg,rgba(181,64,64,0.13),rgba(181,64,64,0.04))",
      border:"2px solid "+C.red,
    }}>
      <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4,flexWrap:"wrap"}}>
        <span style={{fontSize:22}}>☎️</span>
        <div style={{fontSize:16,fontWeight:800,color:C.red,fontFamily:S}}>
          {rows.length} AI booking{rows.length>1?"s":""} need a confirmation call
        </div>
        {neverTried > 0 && (
          <span style={{padding:"3px 10px",background:C.red,color:"#fff",borderRadius:50,fontSize:10,fontWeight:800}}>
            {neverTried} never called
          </span>
        )}
      </div>
      <div style={{fontSize:12,color:C.text,marginBottom:12}}>
        Every AI booking that got a confirmation call kept its appointment. Every one that
        didn&apos;t was cancelled — <strong>46 out of 46</strong>. These stay on the list until you call.
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {rows.map((r) => {
          const stale = !r.confirm_attempts;
          return (
            <div key={r.id} style={{
              display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",
              padding:"11px 13px",background:C.surface,
              border:"1px solid "+(stale?C.red:C.border),borderRadius:9,
              opacity: r.due_now ? 1 : 0.55,
            }}>
              <div style={{flex:"1 1 230px",minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>
                  {r.cn || "Client"}
                  <span style={{fontWeight:500,color:C.muted}}> · {r.treat}</span>
                </div>
                <div style={{fontSize:11.5,color:C.muted,marginTop:2}}>
                  {fmtDate(r.date)} {fmtTime(r.time)} · {r.prov || "—"}
                  {r.days_out != null && <> · in {r.days_out}d</>}
                  {" · "}<span style={{color:stale?C.red:C.muted,fontWeight:stale?700:400}}>{waitingLabel(r.booked_at)}</span>
                  {r.confirm_attempts > 0 && <> · tried {r.confirm_attempts}×</>}
                </div>
              </div>

              {r.phone && (
                <a href={`tel:${r.phone}`} style={{
                  padding:"8px 14px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",
                  color:"#fff",borderRadius:7,fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:F,
                }}>📞 {fmtPhone(r.phone)}</a>
              )}

              <div style={{display:"flex",gap:6}}>
                <button disabled={busy===r.id} onClick={() => confirm(r)} style={{
                  padding:"8px 12px",background:C.greenL,color:C.green,border:"1px solid "+C.green,
                  borderRadius:7,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:F,
                }}>✓ Confirmed</button>
                <button disabled={busy===r.id} onClick={() => noAnswer(r)} style={{
                  padding:"8px 12px",background:C.amberL,color:C.amber,border:"1px solid "+C.amber,
                  borderRadius:7,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:F,
                }}>No answer</button>
                {r.confirm_attempts >= 2 && (
                  <button disabled={busy===r.id} onClick={() => release(r)} style={{
                    padding:"8px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red,
                    borderRadius:7,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:F,
                  }}>Release slot</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{fontSize:11,color:C.muted,marginTop:10}}>
        “No answer” logs the call and brings the row back in 4 hours. After 2 attempts you can
        release the slot so it can be resold.
      </div>
    </div>
  );
}

// ─── Instant popup the moment Sophia books ──────────────────────────────────
const ALERT_ROLES = new Set(["front_desk","director","admin","owner","manager"]);
let _ctx = null, _unlocked = false;
function chime() {
  try {
    if (!_ctx) { const K = window.AudioContext || window.webkitAudioContext; if (K) _ctx = new K(); }
    if (!_ctx) return;
    if (_ctx.state === "suspended") _ctx.resume();
    [0, 0.18].forEach((off, i) => {
      const o = _ctx.createOscillator(), g = _ctx.createGain();
      o.type = "sine"; o.frequency.value = i ? 784 : 587.33;
      g.gain.setValueAtTime(0.0001, _ctx.currentTime + off);
      g.gain.exponentialRampToValueAtTime(0.18, _ctx.currentTime + off + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, _ctx.currentTime + off + 0.5);
      o.connect(g); g.connect(_ctx.destination);
      o.start(_ctx.currentTime + off); o.stop(_ctx.currentTime + off + 0.55);
    });
  } catch (_) { /* audio is a nicety, never break the alert over it */ }
}
if (typeof window !== "undefined" && !_unlocked) {
  const unlock = () => { _unlocked = true; try { if (!_ctx) { const K = window.AudioContext || window.webkitAudioContext; if (K) _ctx = new K(); } _ctx?.resume(); } catch (_) {}
    window.removeEventListener("click", unlock); window.removeEventListener("keydown", unlock); };
  window.addEventListener("click", unlock); window.addEventListener("keydown", unlock);
}

export function ConfirmBookingAlert({ profile, userName }) {
  const [queue, setQueue] = useState([]);
  const seen = useRef(new Set());
  const allowed = ALERT_ROLES.has(String(profile?.role || "").toLowerCase());

  useEffect(() => {
    if (!allowed) return;
    const ch = supabase.channel("ai-booking-alert-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "appointments" }, async ({ new: a }) => {
        if (!a?.booked_by_ai || seen.current.has(a.id)) return;
        seen.current.add(a.id);
        let phone = null;
        if (a.cid) {
          const { data: c } = await supabase.from("clients").select("phone").eq("id", a.cid).maybeSingle();
          phone = c?.phone || null;
        }
        chime();
        setQueue((q) => [...q, { ...a, phone }]);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [allowed]);

  if (!allowed || !queue.length) return null;
  const a = queue[0];
  const dismiss = () => setQueue((q) => q.slice(1));

  const markConfirmed = async () => {
    await supabase.from("appointments").update({
      verbal_confirmed_at: new Date().toISOString(),
      verbal_confirmed_by: userName || "front_desk",
      needs_verbal_confirmation: false,
    }).eq("id", a.id);
    dismiss();
  };

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:99999,background:"rgba(10,10,20,0.62)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:18,
    }}>
      <div style={{
        width:"100%",maxWidth:440,background:C.surface,borderRadius:16,padding:24,
        border:"2px solid "+C.gold,boxShadow:"0 24px 60px rgba(0,0,0,0.35)",fontFamily:F,
      }}>
        <div style={{fontSize:11,letterSpacing:".16em",color:C.gold,fontWeight:800,marginBottom:8}}>
          SOPHIA JUST BOOKED
        </div>
        <div style={{fontSize:21,fontWeight:800,color:C.text,fontFamily:S,marginBottom:3}}>
          {a.cn || "New client"}
        </div>
        <div style={{fontSize:13.5,color:C.muted,marginBottom:14}}>
          {a.treat} · {fmtDate(a.date)} {fmtTime(a.time)}{a.prov ? ` · ${a.prov}` : ""}
        </div>

        <div style={{padding:12,background:C.redL,border:"1px solid "+C.red,borderRadius:9,marginBottom:16}}>
          <div style={{fontSize:12.5,color:C.text,lineHeight:1.5}}>
            <strong>Call now to confirm.</strong> Every AI booking that got a confirmation call
            was kept. Every one that didn&apos;t was cancelled — 46 out of 46.
          </div>
        </div>

        {a.phone && (
          <a href={`tel:${a.phone}`} style={{
            display:"block",textAlign:"center",padding:"14px",marginBottom:9,
            background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",
            borderRadius:9,fontSize:15,fontWeight:800,textDecoration:"none",
          }}>📞 Call {fmtPhone(a.phone)}</a>
        )}

        <div style={{display:"flex",gap:8}}>
          <button onClick={markConfirmed} style={{
            flex:1,padding:"11px",background:C.greenL,color:C.green,border:"1px solid "+C.green,
            borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F,
          }}>✓ Reached &amp; confirmed</button>
          <button onClick={dismiss} style={{
            flex:1,padding:"11px",background:C.light,color:C.muted,border:"1px solid "+C.border,
            borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F,
          }}>Later</button>
        </div>
        <div style={{fontSize:11,color:C.muted,textAlign:"center",marginTop:10}}>
          “Later” keeps it in Today&apos;s Mission until someone calls.
          {queue.length > 1 && <> · {queue.length - 1} more waiting</>}
        </div>
      </div>
    </div>
  );
}
